import {
  Add,
  ChevronLeft,
  ChevronRight,
  Inventory2Outlined,
  PersonOutline,
  Search,
  ViewKanbanOutlined,
} from '@mui/icons-material';
import { IconButton, InputAdornment, TextField, Tooltip } from '@mui/material';
import cn from 'classnames';
import { observer } from 'mobx-react-lite';
import { useState } from 'react';
import { Link, Outlet, useMatch, useNavigate } from 'react-router-dom';

import { Button } from '@/components/Button';
import { IS_V2_WRITE_ENABLED } from '@/configs/v2Write';
import { trackerUrls } from '@/tracker/config/urls';
import { IssueDrawer, useIssueDrawer } from '@/tracker/features/issueDrawer';
import { useTrackerStore } from '@/tracker/model';

import { FavoritesPanel } from '../FavoritesPanel';
import { NavFavorite, NavSection } from '../NavSection';
import { RecentSection } from '../RecentSection';

import s from './TrackerLayoutContent.module.scss';

const ISSUE_KEY = /^[A-Z][A-Z0-9]+-\d+$/;
const SECTIONS: {
  text: string;
  icon: React.ReactNode;
  link: string;
  end: boolean;
  favorites?: 'queues' | 'boards';
}[] = [
  { text: 'Мои задачи', icon: <PersonOutline />, link: trackerUrls.root, end: true },
  {
    text: 'Очереди',
    icon: <Inventory2Outlined />,
    link: trackerUrls.queues,
    end: false,
    favorites: 'queues',
  },
  {
    text: 'Доски',
    icon: <ViewKanbanOutlined />,
    link: trackerUrls.boards,
    end: false,
    favorites: 'boards',
  },
];

const TrackerLayoutContent = observer(() => {
  const { openIssue } = useIssueDrawer();
  const trackerStore = useTrackerStore();
  const navigate = useNavigate();
  // Со страницы очереди задача создаётся в этой очереди
  const queueKey = useMatch(trackerUrls.queue(':queueKey'))?.params.queueKey;
  const newIssueUrl = queueKey ? trackerUrls.newIssueIn(queueKey) : trackerUrls.newIssue;
  const [search, setSearch] = useState('');
  const [isSearchError, setIsSearchError] = useState(false);
  const { isNavCollapsed: isCollapsed, toggleNavCollapsed } = trackerStore;

  const favoriteLinks: Record<'queues' | 'boards', NavFavorite[]> = {
    queues: trackerStore.favoriteQueues.map(({ key, name }) => ({
      id: key,
      label: name,
      to: trackerUrls.queue(key),
    })),
    boards: trackerStore.favoriteBoards.map(({ id, name }) => ({
      id,
      label: name,
      to: trackerUrls.board(id),
    })),
  };

  // shortcut: ищем только по ключу задачи. Полнотекстовый поиск — когда появится в API
  const handleSearchKeyDown = (event: React.KeyboardEvent) => {
    if (event.key !== 'Enter') return;

    const key = search.trim().toUpperCase();

    if (!ISSUE_KEY.test(key)) {
      setIsSearchError(true);

      return;
    }

    setSearch('');
    openIssue(key);
  };

  return (
    <div className={s.layout}>
      <nav className={cn(s.nav, isCollapsed && s.nav_collapsed)} aria-label="Навигация трекера">
        <Tooltip title={isCollapsed ? 'Развернуть панель' : 'Свернуть панель'} placement="right">
          <IconButton
            size="small"
            className={s.nav__toggle}
            aria-label={isCollapsed ? 'Развернуть панель' : 'Свернуть панель'}
            aria-expanded={!isCollapsed}
            onClick={toggleNavCollapsed}
          >
            {isCollapsed ? <ChevronRight /> : <ChevronLeft />}
          </IconButton>
        </Tooltip>

        {IS_V2_WRITE_ENABLED && isCollapsed && (
          <Tooltip title="Создать задачу" placement="right">
            <IconButton
              component={Link}
              to={newIssueUrl}
              size="small"
              aria-label="Создать задачу"
              sx={{ color: 'var(--accent-color)' }}
            >
              <Add />
            </IconButton>
          </Tooltip>
        )}
        {IS_V2_WRITE_ENABLED && !isCollapsed && (
          <Button
            startIcon={<Add />}
            sx={{ width: '100%', mb: 1 }}
            onClick={() => navigate(newIssueUrl)}
          >
            Создать задачу
          </Button>
        )}

        {isCollapsed && (
          <Tooltip title="Поиск по ключу" placement="right">
            <IconButton size="small" aria-label="Поиск по ключу" onClick={toggleNavCollapsed}>
              <Search fontSize="small" />
            </IconButton>
          </Tooltip>
        )}

        {!isCollapsed && (
          <TextField
            size="small"
            sx={{ mb: 1 }}
            placeholder="Ключ задачи: TIME-1"
            value={search}
            error={isSearchError}
            helperText={isSearchError ? 'Введите ключ вида TIME-1' : undefined}
            onChange={(event) => {
              setSearch(event.target.value);
              setIsSearchError(false);
            }}
            onKeyDown={handleSearchKeyDown}
            slotProps={{
              htmlInput: { 'aria-label': 'Перейти к задаче по ключу' },
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <Search fontSize="small" />
                  </InputAdornment>
                ),
              },
            }}
          />
        )}

        {SECTIONS.map(({ text, icon, link, end, favorites }) => (
          <NavSection
            key={link}
            text={text}
            icon={icon}
            link={link}
            end={end}
            isCollapsed={isCollapsed}
            favorites={favorites ? favoriteLinks[favorites] : null}
          />
        ))}

        {!isCollapsed && <RecentSection />}
      </nav>

      <main className={s.content}>
        <Outlet />
      </main>

      <FavoritesPanel queues={favoriteLinks.queues} boards={favoriteLinks.boards} />
      <IssueDrawer />
    </div>
  );
});

export { TrackerLayoutContent };
